/* Dispatch 13 -- DesktopShell.
 *
 * This file is an EXACT preservation of the pre-Dispatch-13 `AppShell`
 * layout. Dispatch 13's contract is: "Do NOT touch DesktopShell". The old
 * `AppShell` moved here byte-for-byte; the new `AppShell` (see AppShell.jsx)
 * picks between this and `MobileShell` based on `useIsMobile()`.
 *
 * If you're tempted to edit this file, ask yourself: does the change apply
 * equally to mobile? If yes, refactor the shared piece out and consume it
 * from both shells. If no, edit MobileShell instead and leave this one
 * alone.
 */
import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { BottomTabBar } from "./BottomTabBar";
import { GlobalCommandPalette } from "./GlobalCommandPalette";
import { QuickCaptureModal } from "./QuickCaptureModal";
import { useTheme } from "@/contexts/ThemeContext";
import { TourAutoLauncher } from "@/components/shell/TourComponents";
import HelpBotLauncher from "@/components/ai/HelpBotLauncher";
import { useAuth } from "@/store/auth";
import { useModuleVisibility } from "@/store/moduleVisibility";
import ModuleGuard from "@/components/ModuleGuard";
import PreviewBanner from "@/components/PreviewBanner";
import PasswordChangeBanner from "@/components/PasswordChangeBanner";

export function DesktopShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { theme } = useTheme();
  const loc = useLocation();
  const user = useAuth((s) => s.user);
  const { loaded, refresh, reset } = useModuleVisibility();

  // Phase 8A — Fetch the per-user visibility matrix once auth is ready.
  const prevUidRef = React.useRef(null);
  useEffect(() => {
    if (user?.id && prevUidRef.current && prevUidRef.current !== user.id) {
      reset();
    }
    prevUidRef.current = user?.id || null;
    if (user?.id && !loaded) refresh();
  }, [user?.id, loaded, refresh, reset]);

  useEffect(() => {
    const handler = () => {
      if (window.innerWidth < 1024) setCollapsed(false);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return (
    <div
      data-testid="desktop-shell"
      className={["min-h-screen", theme === "dark" ? "app-bg-dark" : "app-bg-light"].join(" ")}
    >
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
      <Topbar onCollapseToggle={() => setCollapsed((c) => !c)} />
      <PreviewBanner />
      <PasswordChangeBanner />
      <main
        data-testid="app-main"
        className={[
          "pt-20 pb-24 md:pb-12 transition-all duration-200",
          collapsed ? "lg:pl-[104px]" : "lg:pl-[288px]",
          "pl-4 pr-4 md:px-8",
        ].join(" ")}
      >
        <div key={loc.pathname} className="animate-shell-in">
          <ModuleGuard>
            <Outlet />
          </ModuleGuard>
        </div>
      </main>
      <BottomTabBar />
      <TourAutoLauncher />
      <HelpBotLauncher />
      <GlobalCommandPalette />
      <QuickCaptureModal />
    </div>
  );
}

export default DesktopShell;
